import React, { useState, useEffect, useRef } from 'react';
import { Send, Plus, MessageSquare, Menu, X, Download, AlertTriangle, FileText, Paperclip, Palette, MoreHorizontal, Pencil, Trash2, RefreshCw } from 'lucide-react';
import chatService, { AgentArtifact, ChatMessage, ThreadListItem } from '../services/chatService';
import { getApiBaseUrl } from '../services/apiClient';
import { getEntraAccessToken } from '../services/authService';

interface ChatPageProps {
  navigateTo: (page: string) => void;
  compactMode?: boolean;
  ultraCompactMode?: boolean;
  isReadOnly?: boolean;
  slidesUsed?: number;
  slidesLimit?: number;
  periodEnd?: string | null;
  onUsageUpdate?: () => Promise<void>;
}

const SUGGESTION_CHIPS = [
  "Weekly status report for my team",
  "Market analysis for SaaS industry",
  "Project kickoff deck",
  "Financial quarterly review",
  "Strategy proposal for the board",
];

interface AgentArtifactCardProps {
  artifact: AgentArtifact;
  imageUrls?: Record<string, string>;
  registerContentRef: (key: string, element: HTMLElement | null) => void;
  onContentScroll: (key: string, scrollTop: number) => void;
}

const AgentArtifactCard = React.memo<AgentArtifactCardProps>(({
  artifact,
  imageUrls = {},
  registerContentRef,
  onContentScroll,
}) => {
  const renderArtifactData = () => {
    const renderVisualIssues = (issues: any[]) => (
      issues.length > 0 ? (
        <div className="space-y-2">
          {issues.map((issue: any, index: number) => (
            <div key={index} className="rounded border border-amber-200 bg-amber-50 p-2">
              <div className="text-[11px] font-semibold uppercase text-amber-700">
                {issue.severity}{issue.slide_number ? ` / Slide ${issue.slide_number}` : ''}
              </div>
              <p className="mt-1 text-xs text-gray-700">{issue.description}</p>
              {issue.fix_suggestion && (
                <p className="mt-1 text-xs text-gray-500">{issue.fix_suggestion}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-green-700">No visual issues reported.</p>
      )
    );

    const renderVisualImages = (images: any[], keyPrefix: string) => (
      images.length > 0 ? (
        <div className="grid grid-cols-2 gap-2">
          {images.map((image: any) => {
            const imageKey = `${keyPrefix}:${image.slide_number}`;
            return (
              <div key={imageKey} className="overflow-hidden rounded border border-gray-200 bg-gray-50">
                {imageUrls[imageKey] ? (
                  <img
                    src={imageUrls[imageKey]}
                    alt={`Slide ${image.slide_number}`}
                    className="aspect-video w-full object-contain"
                  />
                ) : (
                  <div className="flex aspect-video items-center justify-center text-xs text-gray-400">
                    Slide {image.slide_number}
                  </div>
                )}
                <div className="px-2 py-1 text-[11px] text-gray-500">Slide {image.slide_number}</div>
              </div>
            );
          })}
        </div>
      ) : null
    );

    if (artifact.key === 'visual_review_result') {
      const issues = artifact.data?.issues || [];
      const images = artifact.data?.images || [];
      return (
        <div className="space-y-3">
          {artifact.data?.summary && (
            <p className="text-xs text-gray-700">{artifact.data.summary}</p>
          )}
          {renderVisualImages(images, 'latest')}
          {renderVisualIssues(issues)}
        </div>
      );
    }

    if (artifact.key === 'visual_review_history') {
      const attempts = Array.isArray(artifact.data) ? artifact.data : [];
      return (
        <div className="space-y-3">
          {attempts.map((attempt: any, index: number) => {
            const attemptNumber = attempt.attempt || index + 1;
            const issues = attempt.issues || [];
            const images = attempt.images || [];
            const statusText = attempt.passed ? 'Passed' : 'Needs attention';
            return (
              <details key={attemptNumber} open={index === attempts.length - 1} className="rounded border border-gray-200 bg-white">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50">
                  <span>Attempt {attemptNumber}</span>
                  <span className="ml-2 font-normal text-gray-500">
                    {attempt.verification_only ? 'Fix verification' : 'Full review'} / {statusText}
                  </span>
                </summary>
                <div className="space-y-3 border-t border-gray-100 p-3">
                  {attempt.summary && (
                    <p className="text-xs text-gray-700">{attempt.summary}</p>
                  )}
                  {renderVisualImages(images, `history:${attemptNumber}`)}
                  {renderVisualIssues(issues)}
                </div>
              </details>
            );
          })}
        </div>
      );
    }

    return (
      <pre
        ref={(element) => registerContentRef(artifact.key, element)}
        onScroll={(event) => onContentScroll(artifact.key, event.currentTarget.scrollTop)}
        className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-700"
      >
        {JSON.stringify(artifact.data, null, 2)}
      </pre>
    );
  };

  return (
    <details open className="rounded-lg border border-gray-200 bg-white">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50">
        <div className="inline-flex flex-col">
          <span>{artifact.title}</span>
          {artifact.summary && (
            <span className="text-xs font-normal text-gray-500">{artifact.summary}</span>
          )}
        </div>
      </summary>
      <div className="border-t border-gray-100 p-3">
        <p className="mb-2 text-xs text-gray-500">{artifact.description}</p>
        {renderArtifactData()}
      </div>
    </details>
  );
}, (previous, next) => (
  previous.artifact === next.artifact
  && previous.imageUrls === next.imageUrls
  && previous.registerContentRef === next.registerContentRef
  && previous.onContentScroll === next.onContentScroll
));

const ChatPage: React.FC<ChatPageProps> = ({
  navigateTo,
  compactMode = false,
  ultraCompactMode = false,
  isReadOnly = false,
  slidesUsed = 0,
  slidesLimit = 0,
  periodEnd = null,
  onUsageUpdate,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [error, setError] = useState<string>('');
  const [chatSidebarVisible, setChatSidebarVisible] = useState(false);
  const [chatHistory, setChatHistory] = useState<ThreadListItem[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [threadsError, setThreadsError] = useState<string>('');
  const [pendingAutoInsert, setPendingAutoInsert] = useState(false);
  const [slidesInserted, setSlidesInserted] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [slideContext, setSlideContext] = useState<string | null>(null);
  const [attachedFile, setAttachedFile] = useState<{ name: string; summary: string } | null>(null);
  const [showThemeBanner, setShowThemeBanner] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [agentArtifacts, setAgentArtifacts] = useState<AgentArtifact[]>([]);
  const [artifactPanelVisible, setArtifactPanelVisible] = useState(false);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const [artifactImageUrls, setArtifactImageUrls] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const artifactPanelScrollRef = useRef<HTMLDivElement>(null);
  const artifactPanelScrollTopRef = useRef(0);
  const artifactContentRefs = useRef<Record<string, HTMLElement | null>>({});
  const artifactContentScrollTopRef = useRef<Record<string, number>>({});
  const visualReviewImageFingerprintRef = useRef('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!artifactPanelVisible || !artifactPanelScrollRef.current) return;
    artifactPanelScrollRef.current.scrollTop = artifactPanelScrollTopRef.current;
  }, [agentArtifacts, artifactImageUrls, artifactPanelVisible]);

  React.useLayoutEffect(() => {
    if (!artifactPanelVisible) return;
    for (const [key, scrollTop] of Object.entries(artifactContentScrollTopRef.current)) {
      const element = artifactContentRefs.current[key];
      if (element) {
        element.scrollTop = scrollTop;
      }
    }
  }, [agentArtifacts, artifactImageUrls, artifactPanelVisible]);

  // Check if user has a custom theme set — show banner if not
  useEffect(() => {
    const dismissed = localStorage.getItem('advisio_theme_banner_dismissed');
    if (dismissed) return;
    (async () => {
      try {
        const token = await getEntraAccessToken();
        if (!token) return;
        const resp = await fetch(`${getApiBaseUrl()}/api/design/active`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (resp.status === 404 || resp.status === 204) {
          setShowThemeBanner(true);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Auto-insert slides when presentation is generated
  useEffect(() => {
    if (pendingAutoInsert && !isLoading && sessionId) {
      setPendingAutoInsert(false);
      insertOrDownloadPresentation(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoInsert, isLoading, sessionId]);

  // Elapsed time counter (progress steps are driven by SSE node_complete events)
  useEffect(() => {
    if (!isLoading) {
      setProgressStep(0);
      setProgressLabel('');
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const elapsed = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(elapsed);
  }, [isLoading]);

  // Load threads from backend
  const loadThreads = async () => {
    setIsLoadingThreads(true);
    setThreadsError('');

    try {
      const result = await chatService.getThreads();
      if (result) {
        setChatHistory(result.threads);
      }
    } catch (error) {
      console.error('Error loading threads:', error);
      setThreadsError(error instanceof Error ? error.message : 'Failed to load chat threads');
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const rememberArtifactScrollPositions = () => {
    if (artifactPanelScrollRef.current) {
      artifactPanelScrollTopRef.current = artifactPanelScrollRef.current.scrollTop;
    }
    for (const [key, element] of Object.entries(artifactContentRefs.current)) {
      if (element) {
        artifactContentScrollTopRef.current[key] = element.scrollTop;
      }
    }
  };

  const registerArtifactContentRef = React.useCallback((key: string, element: HTMLElement | null) => {
    artifactContentRefs.current[key] = element;
    if (element) {
      element.scrollTop = artifactContentScrollTopRef.current[key] || 0;
    }
  }, []);

  const handleArtifactContentScroll = React.useCallback((key: string, scrollTop: number) => {
    artifactContentScrollTopRef.current[key] = scrollTop;
  }, []);

  const mergeArtifacts = (previous: AgentArtifact[], incoming: AgentArtifact[]) => {
    const previousByKey = new Map(previous.map(artifact => [artifact.key, artifact]));
    return incoming.map(artifact => {
      const existing = previousByKey.get(artifact.key);
      if (existing && JSON.stringify(existing) === JSON.stringify(artifact)) {
        return existing;
      }
      return artifact;
    });
  };

  const loadArtifacts = async (threadId: string) => {
    rememberArtifactScrollPositions();
    setIsLoadingArtifacts(true);
    try {
      const result = await chatService.getArtifacts(threadId);
      setAgentArtifacts(prev => mergeArtifacts(prev, result.artifacts));

      const visualReview = result.artifacts.find(a => a.key === 'visual_review_result');
      const visualReviewHistory = result.artifacts.find(a => a.key === 'visual_review_history');
      const visualReviewImageFingerprint = JSON.stringify({
        latest: visualReview?.data || null,
        history: visualReviewHistory?.data || null,
      });
      const images = visualReview?.data?.images || [];
      const nextImageUrls: Record<string, string> = {};
      for (const image of images) {
        if (!image.slide_number) continue;
        try {
          nextImageUrls[`latest:${image.slide_number}`] = await chatService.getVisualReviewImageUrl(threadId, image.slide_number);
        } catch {
          // Image previews are best-effort; artifact JSON still remains visible.
        }
      }
      const visualReviewHistoryData = visualReviewHistory?.data;
      const attempts = Array.isArray(visualReviewHistoryData) ? visualReviewHistoryData : [];
      for (const attempt of attempts) {
        const attemptNumber = attempt.attempt;
        if (!attemptNumber) continue;
        for (const image of attempt.images || []) {
          if (!image.slide_number) continue;
          try {
            nextImageUrls[`history:${attemptNumber}:${image.slide_number}`] = await chatService.getVisualReviewImageUrl(threadId, image.slide_number, attemptNumber);
          } catch {
            // Image previews are best-effort; artifact JSON still remains visible.
          }
        }
      }
      setArtifactImageUrls(prev => {
        const previousKeys = Object.keys(prev);
        const nextKeys = Object.keys(nextImageUrls);
        const sameKeys = previousKeys.length === nextKeys.length && previousKeys.every(key => prev[key] && nextImageUrls[key]);
        if (sameKeys && visualReviewImageFingerprintRef.current === visualReviewImageFingerprint) {
          return prev;
        }
        visualReviewImageFingerprintRef.current = visualReviewImageFingerprint;
        return nextImageUrls;
      });
    } catch (err) {
      console.error('Error loading artifacts:', err);
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  const loadArtifactsFromEvent = (artifact: AgentArtifact) => {
    rememberArtifactScrollPositions();
    setArtifactPanelVisible(true);
    setAgentArtifacts(prev => {
      const existingIndex = prev.findIndex(a => a.key === artifact.key);
      if (existingIndex === -1) return [...prev, artifact];
      const updated = [...prev];
      const nextArtifact = { ...updated[existingIndex], ...artifact };
      if (JSON.stringify(updated[existingIndex]) === JSON.stringify(nextArtifact)) {
        return prev;
      }
      updated[existingIndex] = nextArtifact;
      return updated;
    });

    const match = artifact.data?.url?.match(/\/threads\/([^/]+)\/artifacts/)
      || (artifact as any).url?.match(/\/threads\/([^/]+)\/artifacts/);
    const artifactThreadId = match?.[1] || sessionId;
    if (artifactThreadId) {
      loadArtifacts(artifactThreadId);
    }
  };

  // Load threads when component mounts
  useEffect(() => {
    loadThreads();
  }, []);

  // Upload and parse a file
  const uploadFile = async (file: File) => {
    const allowed = ['xlsx', 'xls', 'csv', 'pdf', 'docx'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!allowed.includes(ext)) {
      setError(`Unsupported file type: .${ext}. Supported: ${allowed.join(', ')}`);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const token = await getEntraAccessToken();
      const formData = new FormData();
      formData.append('file', file);
      const API_BASE_URL = `${getApiBaseUrl()}/api`;
      const resp = await fetch(`${API_BASE_URL}/files/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || `Upload failed: ${resp.status}`);
      }
      const result = await resp.json();
      setAttachedFile({ name: file.name, summary: result.summary || JSON.stringify(result) });
    } catch (err: any) {
      setError(err?.message || 'Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); dragCounterRef.current++; setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); dragCounterRef.current--; if (dragCounterRef.current <= 0) { setIsDragging(false); dragCounterRef.current = 0; } };

  // Silently read current slides from PowerPoint (no UI feedback)
  const autoReadCurrentSlides = async (): Promise<string | null> => {
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const office = (window as any).Office;
        if (!office?.context?.document) { reject(new Error('No Office context')); return; }
        office.context.document.getFileAsync(
          office.FileType.Compressed,
          { sliceSize: 65536 },
          (result: any) => {
            if (result.status !== 'succeeded') { reject(new Error('Failed')); return; }
            const file = result.value;
            const chunks: Uint8Array[] = [];
            let received = 0;
            const getSlice = (idx: number) => {
              file.getSliceAsync(idx, (sr: any) => {
                if (sr.status !== 'succeeded') { file.closeAsync(); reject(new Error('Slice failed')); return; }
                chunks.push(new Uint8Array(sr.value.data));
                received++;
                if (received === file.sliceCount) {
                  file.closeAsync();
                  const total = chunks.reduce((a, c) => a + c.length, 0);
                  const combined = new Uint8Array(total);
                  let off = 0;
                  for (const c of chunks) { combined.set(c, off); off += c.length; }
                  let bin = '';
                  for (let i = 0; i < combined.length; i++) bin += String.fromCharCode(combined[i]);
                  resolve(btoa(bin));
                } else { getSlice(idx + 1); }
              });
            };
            getSlice(0);
          }
        );
      });

      const token = await getEntraAccessToken();
      if (!token) return null;
      const API_BASE_URL = `${getApiBaseUrl()}/api`;
      const resp = await fetch(`${API_BASE_URL}/chat/parse-slides`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64 }),
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      const slides = data.slides as { slide_number: number; texts: string[] }[];
      return slides
        .map(s => `Slide ${s.slide_number}:\n${s.texts.length > 0 ? s.texts.map(t => `  - ${t}`).join('\n') : '  (empty)'}`)
        .join('\n\n');
    } catch { return null; }
  };

  const handleSendMessage = async (messageOverride?: string) => {
    const userText = messageOverride || newMessage;
    if (userText.trim() === '' || isLoading || isReadOnly) return;

    // Show only the user's text in the UI bubble
    const userMessage: ChatMessage = {
      id: Date.now(),
      type: 'user',
      text: userText
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);
    setError('');
    setProgressStep(1);
    const initLabel = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(userText) ? 'リクエストを分析しています...' : 'Analyzing your request...';
    setProgressLabel(initLabel);

    // Auto-detect slide modification requests and read slides automatically
    let messageToSend = userText;
    const isModifyRequest = /スライド\s*\d|slide\s*\d|修正|変更|直して|更新|改善|レイアウト.*変/i.test(userText);

    if (slideContext) {
      // Already have context from manual read
      messageToSend = `${slideContext}\n\nユーザーの修正指示: ${userText}`;
      setSlideContext(null);
    } else if (isModifyRequest && isInPowerPoint()) {
      // Auto-read current slides when modification is requested
      try {
        const slideTexts = await autoReadCurrentSlides();
        if (slideTexts) {
          const context = `以下は現在PowerPointで開いているプレゼンテーションの各スライドの内容です。指定されたスライドのみを修正・生成してください（全スライドを再生成する必要はありません）。\n\n${slideTexts}`;
          messageToSend = `${context}\n\nユーザーの修正指示: ${userText}`;
        }
      } catch (err) {
        console.warn('Auto-read slides failed, sending without context:', err);
      }
    }

    // Prepend attached file context if available
    if (attachedFile) {
      const fileContext = `[添付ファイル: ${attachedFile.name}]\n${attachedFile.summary}`;
      messageToSend = `${fileContext}\n\nユーザーのリクエスト: ${messageToSend}`;
      setAttachedFile(null);
    }

    let presentationGenerated = false;

    // Progress messages from pipeline nodes are shown as visible agent updates.
    // These are the exact messages from backend/app/agent/messages.py
    const PROGRESS_MESSAGES = new Set([
      // Japanese
      'アイデアの方向性を決定しました。ストーリー構成に進みます...',
      'ストーリー構成が完成しました。スライド構成に進みます...',
      'スライド構成が決まりました。デザインを選定しています...',
      'デザインが決定しました。スライドを生成しています...',
      'プレゼンテーションの生成が完了しました。ダウンロードボタンからPPTXファイルを取得できます。',
      'スライドの表示状態を確認しています...',
      '品質チェック完了。プレゼンテーションの準備ができました。',
      '品質を改善しています...',
      // English
      'Creative direction set. Building story structure...',
      'Story structure complete. Creating slide outline...',
      'Slide outline ready. Selecting design system...',
      'Design selected. Generating slides...',
      'Presentation generated. You can download the PPTX file.',
      'Checking rendered slide integrity...',
      'Quality check passed. Your presentation is ready.',
      'Improving quality...',
    ]);
    const isProgressMessage = (text: string) => PROGRESS_MESSAGES.has(text.trim());

    // Node name → user-facing label
    const NODE_LABELS: Record<string, Record<string, string>> = {
      ja: {
        clarify_with_user: 'リクエストを分析しています...',
        creativity_agent: 'アイデアの方向性を検討しています...',
        story_creator: 'ストーリーを構成しています...',
        outline_creator: 'スライド構成を設計しています...',
        design_system_generator: 'デザインを選定しています...',
        slide_creator: 'スライドを生成しています...',
        visual_integrity_reviewer: 'スライドの表示状態を確認しています...',
        reviewer: '品質をチェックしています...',
      },
      en: {
        clarify_with_user: 'Analyzing your request...',
        creativity_agent: 'Developing creative direction...',
        story_creator: 'Building story structure...',
        outline_creator: 'Designing slide outline...',
        design_system_generator: 'Selecting design system...',
        slide_creator: 'Generating slides...',
        visual_integrity_reviewer: 'Checking rendered slide integrity...',
        reviewer: 'Running quality check...',
      },
    };
    const lang = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(userText) ? 'ja' : 'en';

    const callbacks = {
      onMessage: (msg: any) => {
        if (msg.type === 'ai') {
          const text = msg.content || '';
          setMessages(prev => [...prev, {
            id: Date.now() + Math.floor(Math.random() * 10000),
            type: 'system' as const,
            text,
            slideSuggestion: isProgressMessage(text) ? { suggested: false, reason: 'progress' } : undefined,
          }]);
        }
      },
      onNodeComplete: (nodeName: string) => {
        const labels = NODE_LABELS[lang] || NODE_LABELS.en;
        // Show what's coming NEXT (current node just finished)
        const nodeOrder = ['clarify_with_user', 'creativity_agent', 'story_creator', 'outline_creator', 'design_system_generator', 'slide_creator', 'visual_integrity_reviewer', 'reviewer'];
        const idx = nodeOrder.indexOf(nodeName);
        if (idx >= 0 && idx < nodeOrder.length - 1) {
          const nextNode = nodeOrder[idx + 1];
          if (labels[nextNode]) setProgressLabel(labels[nextNode]);
        } else if (nodeName === 'reviewer') {
          // Last node done — show "preparing" until insert completes
          setProgressLabel(lang === 'ja' ? 'プレゼンテーションを準備しています...' : 'Preparing presentation...');
        }
        // Update step for backward compat
        if (nodeName === 'clarify_with_user') setProgressStep(2);
        else if (nodeName === 'outline_creator') setProgressStep(3);
        else if (nodeName === 'slide_creator') setProgressStep(4);
      },
      onArtifactAvailable: (artifact: AgentArtifact) => {
        loadArtifactsFromEvent(artifact);
      },
      onPresentationComplete: () => {
        presentationGenerated = true;
        setMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0) {
            const last = { ...updated[updated.length - 1] };
            last.presentation_id = 'show_download';
            updated[updated.length - 1] = last;
          }
          return updated;
        });
      },
      onError: (errMsg: string) => {
        setError(errMsg);
      },
    };

    try {
      let activeThreadId = sessionId;
      if (!sessionId) {
        // Use the user's text (not the full context) as the thread title
        const threadTitle = userText.length > 60 ? userText.slice(0, 57) + '...' : userText;
        const threadId = await chatService.createThreadStream(messageToSend, threadTitle, callbacks);
        setSessionId(threadId);
        activeThreadId = threadId;
        loadThreads();
      } else {
        await chatService.sendMessageToThreadStream(sessionId, messageToSend, callbacks);
      }

      if (presentationGenerated && activeThreadId) {
        setSlidesInserted(false);
        if (onUsageUpdate) await onUsageUpdate();
        await insertOrDownloadPresentation(activeThreadId);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const rawMsg = error instanceof Error ? error.message : String(error);

      // Map raw errors to user-friendly messages
      let friendlyMsg: string;
      let showRetry = false;
      if (rawMsg.includes('429') || rawMsg.toLowerCase().includes('limit reached')) {
        if (onUsageUpdate) await onUsageUpdate();
        friendlyMsg = 'Slide creation limit reached. Upgrade your plan to continue.';
      } else if (rawMsg.includes('network') || rawMsg.includes('fetch') || rawMsg.includes('Failed to fetch')) {
        friendlyMsg = 'Connection error. Please check your network and try again.';
        showRetry = true;
      } else if (rawMsg.includes('timeout') || rawMsg.includes('TIMEOUT')) {
        friendlyMsg = 'Request timed out. Please try again.';
        showRetry = true;
      } else if (rawMsg === 'UNAUTHORIZED' || rawMsg.includes('401')) {
        friendlyMsg = 'Session expired. Please refresh the page.';
      } else {
        friendlyMsg = 'Something went wrong. Please try again.';
        showRetry = true;
      }
      setError(friendlyMsg);

      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        type: 'system',
        text: `Error: ${friendlyMsg}`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
    setError('');
    setSlidesInserted(false);
    setPendingAutoInsert(false);
    setSlideContext(null);
    setAgentArtifacts([]);
    setArtifactPanelVisible(false);
    setArtifactImageUrls(prev => {
      Object.values(prev).forEach(url => URL.revokeObjectURL(url));
      visualReviewImageFingerprintRef.current = '';
      return {};
    });
  };

  const selectChatSession = async (chatId: string) => {
    setIsLoading(true);
    setError('');
    setSlidesInserted(false);
    setPendingAutoInsert(false);

    try {
      const threadData = await chatService.getThreadById(chatId);

      if (!threadData) {
        throw new Error('Failed to load chat history');
      }

      const loadedMessages: ChatMessage[] = threadData.messages.map((msg, index) => ({
        id: index + 1,
        type: msg.type as 'user' | 'system' | 'assistant',
        text: msg.text,
        timestamp: msg.created_at,
        presentation_id: (threadData.is_presentation_generated && index === threadData.messages.length - 1) ? 'show_download' : undefined
      }));

      setMessages(loadedMessages);
      setSessionId(chatId);
      await loadArtifacts(chatId);
    } catch (error) {
      console.error('Error loading chat session:', error);
      setError(error instanceof Error ? error.message : 'Failed to load chat');

      const errorMessage: ChatMessage = {
        id: 1,
        type: 'system',
        text: `Error loading chat: ${error instanceof Error ? error.message : 'Failed to load chat'}`
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPresentationBase64 = async (threadId: string): Promise<string> => {
    const token = await getEntraAccessToken();
    if (!token) throw new Error('Authentication required. Please log in.');
    const API_BASE_URL = `${getApiBaseUrl()}/api`;
    const response = await fetch(
      `${API_BASE_URL}/chat/threads/${threadId}/presentation?format=base64`,
      { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } },
    );
    if (!response.ok) throw new Error(`Failed to fetch presentation: ${response.status}`);
    const text = await response.text();
    // Remove any whitespace, BOM, or newlines from the base64 string
    const cleaned = text.replace(/[\s\uFEFF]/g, '');
    console.log('Fetched base64 presentation:', cleaned.length, 'chars, starts with:', cleaned.substring(0, 8));
    return cleaned;
  };

  const fetchPresentationBytes = async (threadId: string): Promise<ArrayBuffer> => {
    const token = await getEntraAccessToken();
    if (!token) throw new Error('Authentication required. Please log in.');
    const API_BASE_URL = `${getApiBaseUrl()}/api`;
    const response = await fetch(
      `${API_BASE_URL}/chat/threads/${threadId}/presentation`,
      { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } },
    );
    if (!response.ok) throw new Error(`Failed to fetch presentation: ${response.status}`);
    return response.arrayBuffer();
  };

  // Helper: check if running inside PowerPoint Add-in
  const isInPowerPoint = (): boolean => {
    try {
      const PP = (window as any).PowerPoint;
      const hasRun = !!(PP && typeof PP.run === 'function');
      console.log('[Advisio] isInPowerPoint check:', { hasRun, hasPP: !!PP, hasOffice: !!(window as any).Office });
      return hasRun;
    } catch {
      return false;
    }
  };

  // Guard against duplicate insertions
  const insertingRef = React.useRef(false);

  // Get the ID of the currently selected/displayed slide
  const getCurrentSlideId = async (): Promise<string | null> => {
    const PP = (window as any).PowerPoint;
    if (!PP || typeof PP.run !== 'function') return null;
    try {
      let slideId: string | null = null;
      await PP.run(async (context: any) => {
        const selected = context.presentation.getSelectedSlides();
        selected.load('items/id');
        await context.sync();
        if (selected.items && selected.items.length > 0) {
          slideId = selected.items[selected.items.length - 1].id;
        }
      });
      return slideId;
    } catch (e) {
      console.warn('Could not get current slide ID:', e);
      return null;
    }
  };

  // Core: insert base64 PPTX after the currently displayed slide
  const insertSlides = async (base64String: string): Promise<boolean> => {
    // Prevent concurrent/duplicate insertions
    if (insertingRef.current) {
      console.warn('Insert already in progress, skipping duplicate call');
      return false;
    }
    insertingRef.current = true;

    try {
      const PP = (window as any).PowerPoint;

      if (!PP || typeof PP.run !== 'function') {
        console.error('PowerPoint.run is not available');
        return false;
      }

      const cleanBase64 = base64String.replace(/\s/g, '');

      if (!cleanBase64.startsWith('UEsD')) {
        console.error('Invalid base64: does not start with ZIP magic bytes');
        return false;
      }

      // Get the currently displayed slide to insert after it
      const targetSlideId = await getCurrentSlideId();
      console.log('Inserting slides after slide:', targetSlideId || '(end of presentation)');

      const insertOptions: any = { formatting: "KeepSourceFormatting" };
      if (targetSlideId) {
        insertOptions.targetSlideId = targetSlideId;
      }

      // Primary attempt: insert after current slide with KeepSourceFormatting
      try {
        await PP.run(async function (context: any) {
          context.presentation.insertSlidesFromBase64(cleanBase64, insertOptions);
          await context.sync();
        });
        console.log('Slides inserted after current slide');
        return true;
      } catch (e: any) {
        console.warn('Insert with targetSlideId failed:', e?.code, e?.message);
      }

      // Fallback: insert without position (appends to end)
      try {
        await PP.run(async function (context: any) {
          context.presentation.insertSlidesFromBase64(cleanBase64);
          await context.sync();
        });
        console.log('Slides inserted at end (fallback)');
        return true;
      } catch (e: any) {
        console.warn('Insert fallback failed:', e?.code, e?.message);
      }

      return false;
    } finally {
      insertingRef.current = false;
    }
  };

  // Read current presentation slides and inject text into chat as context
  const readCurrentSlides = async () => {
    if (!isInPowerPoint()) {
      setError('PowerPoint環境で実行してください。');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      // Read the current PPTX file via Office.js common API
      const base64 = await new Promise<string>((resolve, reject) => {
        const office = (window as any).Office;
        office.context.document.getFileAsync(
          office.FileType.Compressed,
          { sliceSize: 65536 },
          (result: any) => {
            if (result.status !== 'succeeded') {
              reject(new Error(result.error?.message || 'Failed to read file'));
              return;
            }
            const file = result.value;
            const sliceCount = file.sliceCount;
            const chunks: Uint8Array[] = [];
            let received = 0;

            const getSlice = (idx: number) => {
              file.getSliceAsync(idx, (sr: any) => {
                if (sr.status !== 'succeeded') {
                  file.closeAsync();
                  reject(new Error(sr.error?.message || 'Failed to read slice'));
                  return;
                }
                chunks.push(new Uint8Array(sr.value.data));
                received++;
                if (received === sliceCount) {
                  file.closeAsync();
                  // Combine all slices → base64
                  const total = chunks.reduce((a, c) => a + c.length, 0);
                  const combined = new Uint8Array(total);
                  let off = 0;
                  for (const c of chunks) { combined.set(c, off); off += c.length; }
                  let bin = '';
                  for (let i = 0; i < combined.length; i++) bin += String.fromCharCode(combined[i]);
                  resolve(btoa(bin));
                } else {
                  getSlice(idx + 1);
                }
              });
            };
            getSlice(0);
          }
        );
      });

      // Send to backend for parsing
      const token = await getEntraAccessToken();
      if (!token) throw new Error('Authentication required');
      const API_BASE_URL = `${getApiBaseUrl()}/api`;
      const resp = await fetch(`${API_BASE_URL}/chat/parse-slides`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64 }),
      });
      if (!resp.ok) throw new Error(`Failed to parse slides: ${resp.status}`);
      const data = await resp.json();

      // Format slide content as text
      const slides = data.slides as { slide_number: number; texts: string[] }[];
      const slideTexts = slides
        .map(s => {
          const body = s.texts.length > 0 ? s.texts.map(t => `  - ${t}`).join('\n') : '  (empty)';
          return `Slide ${s.slide_number}:\n${body}`;
        })
        .join('\n\n');

      // Store as context for the next message sent to the AI.
      // handleSendMessage will prepend this to the user's modification request.
      const contextForAI = `以下は現在PowerPointで開いているプレゼンテーションの各スライドの内容です。指定されたスライドのみを修正・生成してください（全スライドを再生成する必要はありません）。\n\n${slideTexts}`;
      setSlideContext(contextForAI);

      // Show a summary in the chat UI so the user knows slides were read
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system' as const,
        text: `スライドを読み取りました（${slides.length}枚）。修正したい箇所を入力してください。\n\n${slideTexts}`,
      }]);

    } catch (err: any) {
      console.error('Read slides error:', err);
      setError(err?.message || 'スライドの読み取りに失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const insertOrDownloadPresentation = async (threadId: string) => {
    setIsLoading(true);
    setError('');
    try {
      if (isInPowerPoint()) {
        console.log('[Advisio] PowerPoint detected, attempting slide insertion...');
        const base64String = await fetchPresentationBase64(threadId);
        const inserted = await insertSlides(base64String);
        if (inserted) {
          console.log('[Advisio] Slide insertion succeeded');
          setSlidesInserted(true);
        } else {
          console.warn('[Advisio] All insert attempts failed, falling back to download');
          setError('スライドの挿入に失敗しました。ファイルをダウンロードします。');
          const arrayBuffer = await fetchPresentationBytes(threadId);
          downloadFromArrayBuffer(arrayBuffer, threadId);
          setSlidesInserted(true);
        }
      } else {
        console.log('[Advisio] Not in PowerPoint, downloading...');
        const arrayBuffer = await fetchPresentationBytes(threadId);
        downloadFromArrayBuffer(arrayBuffer, threadId);
        setSlidesInserted(true);
      }
    } catch (error) {
      console.error('[Advisio] Presentation error:', error);
      setError(error instanceof Error ? error.message : 'Failed to add presentation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPresentation = async () => {
    if (!sessionId) return;
    setIsLoading(true);
    try {
      const arrayBuffer = await fetchPresentationBytes(sessionId);
      downloadFromArrayBuffer(arrayBuffer, sessionId);
      setSlidesInserted(true);
    } catch (error) {
      console.error('Download error:', error);
      setError(error instanceof Error ? error.message : 'Failed to download presentation');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFromArrayBuffer = (arrayBuffer: ArrayBuffer, threadId?: string) => {
    const blob = new Blob([arrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Use thread title for filename, fallback to ID
    const thread = chatHistory.find(c => c.id === (threadId || sessionId));
    const titleSlug = (thread?.title || 'presentation').replace(/[^a-zA-Z0-9\u3040-\u9FFF]+/g, '_').slice(0, 50);
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `Advisio_${titleSlug}_${dateStr}.pptx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Chat sidebar drawer component (shared between welcome and chat views)
  const ChatSidebar = () => (
    chatSidebarVisible ? (
      <div className="fixed inset-0 z-40" aria-modal="true" role="dialog">
        <div
          className="absolute inset-0 bg-black/30"
          onClick={() => setChatSidebarVisible(false)}
          aria-label="Close sidebar overlay"
        />
        <div className="fixed inset-y-0 left-0 z-50 w-64 sm:w-72 max-w-[85vw] bg-white border-r border-gray-200 shadow-lg">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-sm font-semibold text-gray-700">Chats</h2>
            <button
              onClick={() => setChatSidebarVisible(false)}
              className="p-2 rounded hover:bg-gray-100"
              aria-label="Close sidebar"
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => { startNewChat(); setChatSidebarVisible(false); }}
              disabled={isReadOnly}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isReadOnly
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={isReadOnly ? 'Slide limit reached' : 'Start a new chat'}
            >
              <Plus size={16} />
              New Chat
            </button>
            {isReadOnly && (
              <p className="text-xs text-amber-600 mt-2 text-center">Slide limit reached</p>
            )}
          </div>
          <div className="h-[calc(100%-112px)] overflow-y-auto">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Recent Chats</h3>
              <div className="space-y-2">
                {isLoadingThreads ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Loading chats...</p>
                  </div>
                ) : threadsError ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-red-500 mb-2">{threadsError}</p>
                    <button onClick={loadThreads} className="text-xs text-blue-600 hover:text-blue-800">
                      Try again
                    </button>
                  </div>
                ) : chatHistory.length === 0 ? (
                  <div className="text-center py-4">
                    <MessageSquare size={24} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-xs text-gray-500">No chats yet</p>
                    <p className="text-xs text-gray-400">Start a new chat to begin</p>
                  </div>
                ) : (
                  chatHistory.map(chat => {
                    const ts = new Date(chat.updated_at || chat.created_at);
                    const diff = Date.now() - ts.getTime();
                    const mins = Math.floor(diff / 60000);
                    const hours = Math.floor(mins / 60);
                    const days = Math.floor(hours / 24);
                    const timeAgo = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : mins > 0 ? `${mins}m ago` : 'just now';
                    const displayTitle = chat.title || chat.last_message || 'Untitled Chat';
                    return (
                      <div
                        key={chat.id}
                        className={`group p-2 rounded-lg cursor-pointer transition-colors ${
                          sessionId === chat.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'
                        }`}
                        onClick={async () => {
                          await selectChatSession(chat.id);
                          setChatSidebarVisible(false);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <FileText size={14} className="mt-0.5 flex-shrink-0 text-gray-400" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-800">{displayTitle}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-xs text-gray-400">{timeAgo}</span>
                            </div>
                          </div>
                          {/* Thread actions menu */}
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                            <button
                              type="button"
                              title="Rename"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const newTitle = prompt('Rename chat:', displayTitle);
                                if (newTitle && newTitle.trim()) {
                                  try {
                                    await chatService.renameThread(chat.id, newTitle.trim());
                                    loadThreads();
                                  } catch { /* ignore */ }
                                }
                              }}
                              className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm('Delete this chat? This cannot be undone.')) {
                                  try {
                                    await chatService.deleteThread(chat.id);
                                    if (sessionId === chat.id) {
                                      setSessionId(undefined);
                                      setMessages([]);
                                    }
                                    loadThreads();
                                  } catch { /* ignore */ }
                                }
                              }}
                              className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null
  );

  // Parse choice options from AI message text (e.g. "1) Option A\n2) Option B")
  const parseChoices = (text: string): { preamble: string; choices: { label: string; full: string; isFreeText: boolean }[] } | null => {
    const lines = text.split('\n');
    const choicePattern = /^\s*\*{0,2}([1-9A-Za-z])[)）.]\s*\*{0,2}\s*(.+)/;
    const choices: { label: string; full: string; isFreeText: boolean }[] = [];
    const preambleLines: string[] = [];
    let foundFirst = false;

    for (const line of lines) {
      const match = line.match(choicePattern);
      if (match) {
        foundFirst = true;
        const label = match[1];
        const choiceText = match[2].replace(/\*{2}/g, '').replace(/\s*[-–—]\s*$/, '').trim();
        const isFreeText = /other|その他|自由|記述/i.test(choiceText);
        choices.push({ label, full: choiceText, isFreeText });
      } else if (!foundFirst && line.trim()) {
        preambleLines.push(line);
      }
    }

    if (choices.length >= 2) {
      return { preamble: preambleLines.join('\n'), choices };
    }
    return null;
  };

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const agentWorkPanel = (
    artifactPanelVisible ? (
      <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-white border-l border-gray-200 shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Agent Work</h2>
            <p className="text-xs text-gray-500">Intermediate artifacts from each step</p>
          </div>
          <button
            type="button"
            onClick={() => setArtifactPanelVisible(false)}
            className="p-2 rounded hover:bg-gray-100"
            aria-label="Close agent work panel"
          >
            <X size={16} />
          </button>
        </div>
        <div
          ref={artifactPanelScrollRef}
          onScroll={(event) => {
            artifactPanelScrollTopRef.current = event.currentTarget.scrollTop;
          }}
          className="flex-1 overflow-y-auto p-3"
        >
          {isLoadingArtifacts && agentArtifacts.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading artifacts...</div>
          ) : agentArtifacts.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">No artifacts yet.</div>
          ) : (
            <div className="space-y-3">
              {agentArtifacts.map(artifact => (
                <AgentArtifactCard
                  key={artifact.key}
                  artifact={artifact}
                  imageUrls={
                    artifact.key === 'visual_review_result'
                    || artifact.key === 'visual_review_history'
                      ? artifactImageUrls
                      : undefined
                  }
                  registerContentRef={registerArtifactContentRef}
                  onContentScroll={handleArtifactContentScroll}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    ) : null
  );

  const MessageWithActions: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isUser = message.type === 'user';
    const isLatestAI = !isUser && message === messages.filter(m => m.type === 'system').slice(-1)[0];
    const parsed = !isUser && isLatestAI && !isLoading ? parseChoices(message.text) : null;

    return (
      <div className={`flex px-3 sm:px-4 py-2 sm:py-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] sm:max-w-[75%] ${isUser ? 'order-1' : 'order-1'}`}>
          <div className={`rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
            isUser
              ? 'bg-blue-600 text-white rounded-br-md'
              : 'bg-white border border-gray-200 text-black rounded-bl-md shadow-sm'
          }`}>
            <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed break-words-safe" style={!isUser ? { color: '#000000' } : undefined}>
              {parsed ? parsed.preamble : message.text}
            </p>
            {parsed && (
              <div className="mt-2 flex flex-col gap-1.5">
                {parsed.choices.map((choice, idx) => (
                  choice.isFreeText ? (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => textareaRef.current?.focus()}
                      className="w-full text-left px-3 py-2 text-xs sm:text-sm rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors"
                    >
                      {choice.label}) {choice.full}
                    </button>
                  ) : (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSendMessage(choice.full)}
                      disabled={isLoading}
                      className="w-full text-left px-3 py-2 text-xs sm:text-sm rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 transition-colors disabled:opacity-50"
                    >
                      {choice.label}) {choice.full}
                    </button>
                  )
                ))}
              </div>
            )}
          </div>

          {message.presentation_id && (() => {
            // Detect language from user messages in the thread
            const userMsgs = messages.filter(m => m.type === 'user').map(m => m.text).join('');
            const isJa = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(userMsgs);
            const t = {
              title: isJa ? 'プレゼンテーション生成完了' : 'Presentation Generated',
              inserted: isJa ? 'スライドが追加されました。' : 'Slides have been added.',
              processing: isJa ? '処理中...' : 'Processing...',
              ready: isJa ? 'プレゼンテーションの準備ができました' : 'Your presentation is ready',
              insert: isJa ? 'スライドを挿入' : 'Insert Slides',
              inserting: isJa ? '挿入中...' : 'Inserting...',
              download: isJa ? 'ダウンロード' : 'Download',
            };
            return (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {t.title}
                </span>
              </div>
              <p className="text-sm text-blue-700 mb-3">
                {slidesInserted ? t.inserted : isLoading ? t.processing : t.ready}
              </p>
              <div className="flex flex-wrap gap-2">
                {isInPowerPoint() && (
                  <>
                    <button
                      onClick={() => sessionId && insertOrDownloadPresentation(sessionId)}
                      disabled={isLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <Plus size={14} />
                      {isLoading ? t.inserting : t.insert}
                    </button>
                  </>
                )}
                <button
                  onClick={handleDownloadPresentation}
                  disabled={isLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 bg-gray-600 text-white hover:bg-gray-700"
                >
                  <Download size={14} />
                  {t.download}
                </button>
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // Welcome screen (no messages yet and no session)
  const isWelcomeScreen = messages.length === 0 && !sessionId;

  return (
    <div className="flex h-screen bg-gray-50">
      <ChatSidebar />
      {agentWorkPanel}

      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-2 sm:p-3 md:p-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3 min-w-0 flex-1">
            <button
              onClick={() => setChatSidebarVisible(true)}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Open chat sidebar"
              title="Open menu"
            >
              <Menu size={18} className="sm:w-5 sm:h-5" />
            </button>
            <div className="min-w-0 hidden sm:block">
              <h2 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 truncate">Advisio AI</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                setArtifactPanelVisible(true);
                if (sessionId) loadArtifacts(sessionId);
              }}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
              title="View agent work artifacts"
            >
              <FileText size={13} />
              Agent Work{agentArtifacts.length > 0 ? ` (${agentArtifacts.length})` : ''}
            </button>
            <span className="px-2 sm:px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full whitespace-nowrap">
              Online
            </span>
          </div>
        </div>

        {/* Read Only Banner */}
        {isReadOnly && (
          <div className="bg-amber-50 border-b border-amber-200 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-amber-800">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <span>
                You have reached your slide limit ({slidesUsed}/{slidesLimit === 0 ? 'Unlimited' : slidesLimit}) for this billing period.
                You can view existing chats and download presentations, but cannot create new content.
                {periodEnd && ` Resets on ${new Date(periodEnd).toLocaleDateString()}.`}
              </span>
            </div>
          </div>
        )}

        {/* Content area */}
        {isWelcomeScreen ? (
          /* Welcome Screen */
          <div className="flex-1 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <div className="max-w-lg w-full text-center">
              <svg width="40" height="40" viewBox="0 0 375 374.999991" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-4">
                <defs>
                  <clipPath id="welcome-a"><path d="M 23.988281 232 L 350.988281 232 L 350.988281 365 L 23.988281 365 Z" clipRule="nonzero"/></clipPath>
                  <clipPath id="welcome-b"><path d="M 76 232 L 350.988281 232 L 350.988281 365 L 76 365 Z" clipRule="nonzero"/></clipPath>
                  <clipPath id="welcome-c"><path d="M 23.988281 232 L 188 232 L 188 308 L 23.988281 308 Z" clipRule="nonzero"/></clipPath>
                  <clipPath id="welcome-d"><path d="M 76 10 L 350.988281 10 L 350.988281 143 L 76 143 Z" clipRule="nonzero"/></clipPath>
                  <clipPath id="welcome-e"><path d="M 23.988281 67 L 188 67 L 188 143 L 23.988281 143 Z" clipRule="nonzero"/></clipPath>
                </defs>
                <g clipPath="url(#welcome-a)"><path fill="#1e57a3" d="M 238.792969 232.34375 L 187.414062 283.726562 L 136.03125 232.34375 L 23.972656 232.34375 L 134.644531 343.011719 C 163.675781 372.039062 211.171875 372.039062 240.199219 343.011719 L 350.863281 232.34375 Z" fillOpacity="1" fillRule="evenodd"/></g>
                <g clipPath="url(#welcome-b)"><path fill="#1e57a3" d="M 187.402344 283.726562 C 156.78125 314.359375 119.929688 314.863281 76.847656 285.230469 L 134.625 343.011719 C 163.652344 372.039062 211.152344 372.039062 240.179688 343.011719 L 350.839844 232.34375 L 238.78125 232.34375 Z" fillOpacity="1" fillRule="evenodd"/></g>
                <g clipPath="url(#welcome-c)"><path fill="#007aff" d="M 76.859375 285.230469 C 119.9375 314.863281 156.792969 314.359375 187.414062 283.726562 L 136.035156 232.34375 L 23.972656 232.34375 L 76.871094 285.242188 Z" fillOpacity="1" fillRule="evenodd"/></g>
                <g clipPath="url(#welcome-d)"><path fill="#1e57a3" d="M 187.402344 91.324219 C 156.78125 60.691406 119.929688 60.1875 76.847656 89.816406 L 134.625 32.039062 C 163.652344 3.007812 211.152344 3.007812 240.179688 32.039062 L 350.851562 142.707031 L 238.792969 142.707031 Z" fillOpacity="1" fillRule="evenodd"/></g>
                <g clipPath="url(#welcome-e)"><path fill="#007aff" d="M 76.859375 89.816406 C 119.9375 60.1875 156.792969 60.691406 187.414062 91.324219 L 136.035156 142.707031 L 23.972656 142.707031 Z" fillOpacity="1" fillRule="evenodd"/></g>
              </svg>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                What would you like to present?
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                Describe your presentation topic and I'll create professional consulting-quality slides.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTION_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    onClick={() => handleSendMessage(chip)}
                    disabled={isLoading || isReadOnly}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-full text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Messages */
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {messages.map(message => (
              <MessageWithActions key={message.id} message={message} />
            ))}
            {isLoading && progressLabel && (
              <div className="flex items-center gap-2 px-4 py-3">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <span className="text-sm text-gray-500">{progressLabel}</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Theme setup banner */}
        {showThemeBanner && (
          <div className="mx-2 sm:mx-3 md:mx-4 mt-2 flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
            <Palette size={14} className="flex-shrink-0" />
            <span className="flex-1">
              Upload your company template for branded slides.
            </span>
            <button
              type="button"
              onClick={() => navigateTo('design')}
              className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 flex-shrink-0"
            >
              Set up
            </button>
            <button
              type="button"
              onClick={() => { setShowThemeBanner(false); localStorage.setItem('advisio_theme_banner_dismissed', '1'); }}
              className="text-purple-400 hover:text-purple-600 flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Message Input */}
        <div
          className={`relative bg-white border-t p-2 sm:p-3 md:p-4 transition-all ${isDragging ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          {isDragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg pointer-events-none">
              <div className="text-center">
                <Paperclip size={24} className="mx-auto text-blue-500 mb-1" />
                <p className="text-sm font-medium text-blue-600">Drop file here</p>
                <p className="text-xs text-blue-400">Excel, CSV, PDF, Word</p>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.pdf,.docx"
            onChange={handleFileAttach}
            className="hidden"
          />
          {/* Composer card */}
          <div className={`border rounded-xl overflow-hidden transition-colors ${isReadOnly ? 'border-amber-300 bg-amber-50' : 'border-gray-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'}`}>
            {/* Attached file badge */}
            {attachedFile && (
              <div className="mx-3 mt-2 flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <FileText size={12} />
                <span className="flex-1 truncate">{attachedFile.name}</span>
                <button type="button" onClick={() => setAttachedFile(null)} className="text-blue-400 hover:text-blue-600">
                  <X size={12} />
                </button>
              </div>
            )}
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isReadOnly) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              rows={2}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
              placeholder={isReadOnly ? "Slide limit reached. Upgrade your plan." : "Describe the presentation you want to create..."}
              className="w-full px-3 pt-3 pb-1 text-sm bg-transparent border-none focus:outline-none focus:ring-0 resize-none min-w-0"
              disabled={isLoading || isReadOnly}
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
            {/* Bottom toolbar: attach left, send right */}
            <div className="flex items-center justify-between px-2 pb-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || isReadOnly}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                aria-label="Attach file"
                title="Attach file (Excel, CSV, PDF, Word)"
              >
                <Paperclip size={18} />
              </button>
              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={isLoading || newMessage.trim() === '' || isReadOnly}
                className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs sm:text-sm text-red-700 break-words-safe flex items-center gap-2">
              <span className="flex-1">{error}</span>
              {error.includes('try again') && (
                <button
                  type="button"
                  onClick={() => { setError(''); handleSendMessage(); }}
                  className="flex-shrink-0 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  <RefreshCw size={12} className="inline mr-1" />Retry
                </button>
              )}
              <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
